import { ROUTES } from "@/constants/routes";
import { useClosePane } from "@/hooks/pane-routing";
import { useIsPublicCanvasView } from "@/public-canvas/PublicCanvasArtifactContext";
import { route } from "@/utils/route";
import type {
  CopyFileRecordPayload,
  CreateFileRecordPayload,
  FileRecord,
  FileRecordComponents,
  FileRecordVersion,
  MoveFileRecordPayload,
  PatchFileRecordRegionPayload,
  PatchFileRecordRegionResponse,
  PresignUploadResponse,
  PublishManyResult,
  UnpublishManyResult,
} from "@cohesive/api-client";
import type { Entity } from "@cohesive/api-entities";
import {
  getPresignedUrlExpiresAtMs,
  getPresignedUrlRefreshDelayMs,
} from "@cohesive/ui-components";
import {
  useMutation,
  UseMutationOptions,
  useQueries,
  useQuery,
  useQueryClient,
  UseQueryOptions,
} from "@tanstack/react-query";
import { AxiosError, isAxiosError } from "axios";
import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { orgAPI } from "./api";

/** Refetch the file record before its presigned CloudFront URL expires. */
function usePresignedFileRecordUrlRefresh(
  fileRecordId: string | undefined,
  url: string | undefined,
) {
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!fileRecordId || !url) return;

    const expiresAtMs = getPresignedUrlExpiresAtMs(url);
    if (expiresAtMs == null) return;

    const scheduleRefresh = () => {
      const delay = getPresignedUrlRefreshDelayMs(expiresAtMs, Date.now());
      if (delay <= 0) {
        void queryClient.refetchQueries({
          queryKey: fileRecordKeys.detail(fileRecordId),
        });
        return null;
      }
      return window.setTimeout(() => {
        void queryClient.refetchQueries({
          queryKey: fileRecordKeys.detail(fileRecordId),
        });
      }, delay);
    };

    let timeoutId = scheduleRefresh();
    return () => {
      if (timeoutId != null) window.clearTimeout(timeoutId);
    };
  }, [fileRecordId, url, queryClient]);
}

export type RecoverableScope = "workspace" | "canvas";

export const fileRecordKeys = {
  all: ["fileRecords"] as const,
  recent: (limit?: number) =>
    limit === undefined
      ? (["fileRecords", "recent"] as const)
      : (["fileRecords", "recent", limit] as const),
  workspace: (workspaceId: string) =>
    ["fileRecords", "workspace", workspaceId] as const,
  canvas: (canvasId: string) => ["fileRecords", "canvas", canvasId] as const,
  sharedWithMe: () => ["fileRecords", "sharedWithMe"] as const,
  flowSession: (flowSessionId: string) =>
    ["fileRecords", "flowSession", flowSessionId] as const,
  recentlyDeleted: (scope: RecoverableScope, scopeId: string) =>
    ["fileRecords", "recentlyDeleted", scope, scopeId] as const,
  detail: (id: string) => ["fileRecord", id] as const,
  tiptapToken: (id: string) =>
    [...fileRecordKeys.detail(id), "tiptapToken"] as const,
  collaborationToken: (id: string) =>
    [...fileRecordKeys.detail(id), "collaborationToken"] as const,
  versions: (id: string) => [...fileRecordKeys.detail(id), "versions"] as const,
  presignedContent: (url: string) =>
    [...fileRecordKeys.all, "presignedContent", url] as const,
};

/** API returns this while the file record is still processing. */
const TIPTAP_COLLAB_PROCESSING_PENDING_MESSAGE =
  "Collaboration is only available for files that have finished processing.";

const COLLABORATION_SETUP_MAX_RETRIES = 90;

export function isTiptapCollaborationProcessingPendingError(
  err: unknown,
): boolean {
  if (
    err instanceof Error &&
    err.message === TIPTAP_COLLAB_PROCESSING_PENDING_MESSAGE
  )
    return true;
  if (!isAxiosError(err)) return false;
  const data = err.response?.data as
    | { error?: { message?: string } }
    | undefined;
  return data?.error?.message === TIPTAP_COLLAB_PROCESSING_PENDING_MESSAGE;
}

/** Matches backend UserMessage when TipTap HEAD finds no document yet. */
const TIPTAP_ENABLE_DOCUMENT_NOT_CREATED_MESSAGE =
  "Collaboration document has not been created yet.";

export function isTiptapEnableDocumentNotCreatedError(err: unknown): boolean {
  if (
    err instanceof Error &&
    err.message === TIPTAP_ENABLE_DOCUMENT_NOT_CREATED_MESSAGE
  )
    return true;
  if (!isAxiosError(err)) return false;
  // Backend migrated this from 400 to 422 (transient "not ready yet" state).
  // Accept both during rollout so retries keep working regardless of
  // which side ships first.
  const status = err.response?.status;
  if (status !== 400 && status !== 422) return false;
  const data = err.response?.data as
    | { error?: { message?: string } }
    | undefined;
  return data?.error?.message === TIPTAP_ENABLE_DOCUMENT_NOT_CREATED_MESSAGE;
}

/** Thrown from {@link useFileRecordCollaborationToken} when the tiptap-token response has no `document_name`. */
export class FileRecordTiptapDocumentNamePendingError extends Error {
  readonly name = "FileRecordTiptapDocumentNamePendingError";
  constructor() {
    super("Tiptap document name not yet available for this file record.");
  }
}

/** Thrown from {@link useFileRecordCollaborationToken} when `getById` has a status other than `processed`. */
export class FileRecordStatusNotProcessedError extends Error {
  readonly name = "FileRecordStatusNotProcessedError";
  constructor() {
    super("File record is not processed yet.");
  }
}

export function isFileRecordTiptapDocumentNamePendingError(
  err: unknown,
): err is FileRecordTiptapDocumentNamePendingError {
  return err instanceof FileRecordTiptapDocumentNamePendingError;
}

export function isFileRecordStatusNotProcessedError(
  err: unknown,
): err is FileRecordStatusNotProcessedError {
  return err instanceof FileRecordStatusNotProcessedError;
}

export type FileRecordCollaborationTokenResult = {
  fileRecord: FileRecord;
  token: string;
  can_write: boolean;
  expires_at: string;
  document_name: string;
  tiptap_enabled: boolean;
};

function collaborationSetupRetry(
  failureCount: number,
  error: unknown,
): boolean {
  if (failureCount >= COLLABORATION_SETUP_MAX_RETRIES) return false;
  return (
    isFileRecordTiptapDocumentNamePendingError(error) ||
    isFileRecordStatusNotProcessedError(error) ||
    isTiptapCollaborationProcessingPendingError(error)
  );
}

/**
 * Loads the file record, then mints a TipTap collab JWT. Retries when the
 * token response lacks `document_name` or when the token endpoint reports processing not finished.
 */
export function useFileRecordCollaborationToken(
  fileRecordId: string | undefined,
  options?: Pick<
    UseQueryOptions<FileRecordCollaborationTokenResult>,
    "enabled"
  >,
) {
  return useQuery({
    queryKey: fileRecordKeys.collaborationToken(fileRecordId ?? ""),
    queryFn: async (): Promise<FileRecordCollaborationTokenResult> => {
      if (!fileRecordId) throw new Error("File record id required");
      const fileRecord = await orgAPI.fileRecords.getById(fileRecordId);

      if (fileRecord.status !== "processed") {
        throw new FileRecordStatusNotProcessedError();
      }

      const tokenPayload = await orgAPI.fileRecords.tiptapToken(fileRecordId);
      if (!tokenPayload.document_name?.trim()) {
        throw new FileRecordTiptapDocumentNamePendingError();
      }
      const { token, can_write, expires_at, document_name, tiptap_enabled } =
        tokenPayload;
      return {
        fileRecord,
        token,
        can_write,
        expires_at,
        document_name,
        tiptap_enabled,
      };
    },
    enabled: !!fileRecordId && (options?.enabled ?? true),
    staleTime: 0,
    gcTime: 10 * 60 * 1000,
    refetchOnWindowFocus: false,
    retry: collaborationSetupRetry,
    retryDelay: (attemptIndex) =>
      Math.min(1000 * 2 ** Math.min(attemptIndex, 5), 30_000),
  });
}

/**
 * Loads raw markdown from a file record presigned GET URL (e.g. S3).
 * Intended for collaboration seeding when the TipTap room does not exist yet.
 */
export function useFileRecordContent(
  url: string | null | undefined,
  options?: Pick<UseQueryOptions<string>, "enabled">,
) {
  const trimmed = url?.trim() ?? "";
  return useQuery({
    queryKey: trimmed
      ? fileRecordKeys.presignedContent(trimmed)
      : ([...fileRecordKeys.all, "presignedContent", "__none__"] as const),
    queryFn: async () => {
      if (!trimmed) {
        throw new Error("File record content URL required");
      }
      const res = await fetch(trimmed);
      if (!res.ok) {
        throw new Error(
          `Failed to fetch file record content: HTTP ${res.status}`,
        );
      }
      return res.text();
    },
    enabled: !!trimmed && (options?.enabled ?? true),
    staleTime: 0,
    gcTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    retry: false,
  });
}

/** Shared options for minting / refreshing the TipTap JWT (see {@link useFileRecordTiptapToken}). */
export function fileRecordTiptapTokenQueryOptions(fileRecordId: string) {
  return {
    queryKey: fileRecordKeys.tiptapToken(fileRecordId),
    queryFn: () => orgAPI.fileRecords.tiptapToken(fileRecordId),
  };
}

/**
 * TipTap Document Server JWT for a file record. Retries while the API reports
 * that collaboration is not ready until processing finishes.
 */
export function useFileRecordTiptapToken(
  fileRecordId: string | undefined,
  options?: { enabled?: boolean },
) {
  return useQuery({
    ...fileRecordTiptapTokenQueryOptions(fileRecordId ?? ""),
    enabled: !!fileRecordId && (options?.enabled ?? true),
    staleTime: 0,
    refetchOnWindowFocus: false,
    retry: (failureCount, error) =>
      isTiptapCollaborationProcessingPendingError(error) &&
      failureCount < COLLABORATION_SETUP_MAX_RETRIES,
    retryDelay: () => 2000,
  });
}

/**
 * Batch-fetch file records by id, reusing per-id caches across call sites.
 *
 * Designed for the chat-message rendering path: a `Message.file_record_ids`
 * list becomes an array of parallel queries, one per id. Each id gets its
 * own `fileRecordKeys.detail(id)` cache key, so the same file referenced
 * on multiple messages or tabs only fetches once.
 *
 * Missing / deleted records surface as `error` on their individual query
 * entry (callers render a "file unavailable" card for those without failing
 * the whole list).
 */
export function useFileRecordsByIds(
  ids: string[] | undefined,
  options?: {
    /**
     * react-query `retry` for each per-id query. Pass `false` when the
     * ids may legitimately not resolve (e.g. an HTML doc embeds files
     * from another account) so a 404 settles on the first attempt
     * instead of retrying — callers can then fall back immediately.
     */
    retry?: boolean | number;
  },
) {
  const uniqueIds = Array.from(new Set((ids ?? []).filter(Boolean)));
  return useQueries({
    queries: uniqueIds.map((id) => ({
      queryKey: fileRecordKeys.detail(id),
      queryFn: () => orgAPI.fileRecords.getById(id),
      ...(options?.retry !== undefined ? { retry: options.retry } : {}),
    })),
  });
}

/**
 * Fetch a single FileRecord by id.
 *
 * The detail pane uses this to get a fresh presigned `url` for playback /
 * preview. Presigned URLs expire (typical TTL 5–15 min), so we set a
 * conservative `staleTime` of 4 minutes: opening the pane after that
 * refetches in the background while serving the cached record immediately.
 * Presigned URLs expire after ~1h; `usePresignedFileRecordUrlRefresh` also
 * refetches shortly before the URL's `Expires` query param.
 * `gcTime` is bumped so cache entries survive a quick pane close/reopen.
 */
/**
 * `FileRecord` (api-client) uses `path` instead of `title`, but
 * `BaseFileRecord` (api-entities) requires `title` on the entity data
 * payload. Derive it from the path leaf once, here, so every builder below
 * is consistent.
 */
function fileRecordToEntityData(record: FileRecord) {
  const segments = record.path?.split("/").filter(Boolean) ?? [];
  const title = segments[segments.length - 1] || record.id;
  return { ...record, title };
}

function isCompleteFileRecordWire(d: unknown): d is FileRecord {
  if (!d || typeof d !== "object") return false;
  const o = d as Partial<FileRecord>;
  return (
    typeof o.id === "string" &&
    typeof o.path === "string" &&
    o.path.length > 0 &&
    typeof o.content_type === "string" &&
    typeof o.organization_id === "string"
  );
}

/**
 * Accepts either:
 *  - a `base: "file_record"` entity (wire payload — just `{id, title}`) and
 *    enriches it with the full `FileRecord` row (presigned URL, metadata, …).
 *    Skips the fetch when `data` already looks like a complete row.
 *  - a plain `id` string, building the entity from scratch after fetching.
 */
export function useHydrateFileRecordEntity(
  input:
    | string
    | { base: "file_record"; data: unknown; platform: string }
    | null
    | undefined,
): {
  entity: Entity | null;
  isLoading: boolean;
  isError: boolean;
} {
  const isPublicCanvasView = useIsPublicCanvasView();
  const isIdMode = typeof input === "string";
  const entity = isIdMode ? null : (input ?? null);

  const raw = entity?.data;
  const id = isIdMode
    ? input
    : raw && typeof raw === "object" && "id" in raw
      ? String((raw as { id: unknown }).id)
      : "";

  const alreadyComplete =
    !isIdMode && !!entity && isCompleteFileRecordWire(raw);

  const wireRecord = alreadyComplete ? (raw as FileRecord) : null;

  const {
    data: freshData,
    isLoading,
    isError,
  } = useFileRecord(id || undefined, {
    // Public share SPA has no API auth — use CDN-enriched entity payload only.
    enabled: !!id && !isPublicCanvasView,
  });

  if (!input) return { entity: null, isLoading: false, isError: false };
  if (!id) return { entity: null, isLoading: false, isError: false };

  if (isPublicCanvasView) {
    if (!isIdMode && entity) {
      return {
        entity: entity as unknown as Entity,
        isLoading: false,
        isError: false,
      };
    }
    return { entity: null, isLoading: false, isError: true };
  }

  // Show the Loro-persisted row immediately; merge API data when it arrives so
  // presigned URLs stay fresh on long-lived canvas nodes.
  if (wireRecord && !freshData) {
    if (isLoading) {
      return {
        entity: {
          type: "file_record",
          base: "file_record",
          platform: entity!.platform ?? "cohesive",
          data: fileRecordToEntityData(wireRecord),
        } as unknown as Entity,
        isLoading: false,
        isError: false,
      };
    }
    if (isError) {
      return {
        entity: {
          type: "file_record",
          base: "file_record",
          platform: entity!.platform ?? "cohesive",
          data: fileRecordToEntityData(wireRecord),
        } as unknown as Entity,
        isLoading: false,
        isError: false,
      };
    }
  }

  if (isLoading && !freshData && !wireRecord) {
    return { entity: null, isLoading: true, isError: false };
  }
  if (isError && !freshData && !wireRecord) {
    return { entity: null, isLoading: false, isError: true };
  }

  const record = freshData ?? wireRecord;
  if (!record) {
    return { entity: null, isLoading: false, isError: false };
  }

  const baseData = isIdMode ? {} : typeof raw === "object" && raw ? raw : {};
  return {
    entity: {
      type: "file_record",
      base: "file_record",
      platform: (!isIdMode && entity?.platform) || "cohesive",
      data: { ...baseData, ...fileRecordToEntityData(record) },
    } as unknown as Entity,
    isLoading: false,
    isError: false,
  };
}

export function useFileRecord(
  fileRecordId: string | undefined,
  options?: Partial<UseQueryOptions<FileRecord, AxiosError>>,
) {
  const query = useQuery({
    queryKey: fileRecordKeys.detail(fileRecordId ?? ""),
    queryFn: () => {
      if (!fileRecordId) throw new Error("FileRecord id required");
      return orgAPI.fileRecords.getById(fileRecordId);
    },
    enabled: !!fileRecordId,
    staleTime: 4 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    refetchOnWindowFocus: true,
    ...options,
  });

  usePresignedFileRecordUrlRefresh(fileRecordId, query.data?.url);

  return query;
}

/**
 * Fetch all file records for a workspace.
 *
 * Polling is opt-in: pass `refetchInterval` via `options` when you need
 * live updates (e.g. the upload queue does this until pending uploads
 * transition to processed). By default this query does NOT poll — image
 * previews etc. were flashing on every refetch and we don't need real-time
 * updates for general browsing today.
 *
 * TODO: add cursor-based pagination if the backend exposes it.
 */

export function useWorkspaceFileRecords(
  workspaceId: string | undefined,
  options?: Partial<UseQueryOptions<FileRecord[], AxiosError>>,
) {
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: fileRecordKeys.workspace(workspaceId ?? ""),
    queryFn: () => {
      if (!workspaceId) throw new Error("Workspace ID required");
      return orgAPI.fileRecords.list({
        class: "workspace",
        workspace_id: workspaceId,
      });
    },
    ...options,
    enabled: !!workspaceId && (options?.enabled ?? true),
  });

  useEffect(() => {
    if (!query.data) return;
    for (const r of query.data) {
      queryClient.setQueryData(fileRecordKeys.detail(r.id), r);
    }
  }, [query.data, queryClient]);

  return query;
}

/**
 * Files the user created (folders excluded), most recently modified first,
 * scoped server-side to workspaces and canvases they can still view plus their
 * own chats. Backs the home browser's Files · Recents view. `limit` caps the
 * page (server default 20, max 100); the endpoint supports `before_ts` keyset
 * pagination for future load-more.
 */
export function useRecentFileRecords(
  limit = 50,
  options?: Partial<UseQueryOptions<FileRecord[], AxiosError>>,
) {
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: fileRecordKeys.recent(limit),
    queryFn: async () => {
      const response = await orgAPI.fileRecords.listRecent({ limit });
      return response.file_records;
    },
    ...options,
  });

  useEffect(() => {
    if (!query.data) return;
    for (const r of query.data) {
      queryClient.setQueryData(fileRecordKeys.detail(r.id), r);
    }
  }, [query.data, queryClient]);

  return query;
}

/**
 * Fetch file records shared directly with the current user (SpiceDB
 * viewer/editor grants on the file itself), independent of parent-scope access.
 * Powers the "Shared with me" section in the Files rail.
 */
export function useSharedWithMeFileRecords(
  options?: Partial<UseQueryOptions<FileRecord[], AxiosError>>,
) {
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: fileRecordKeys.sharedWithMe(),
    queryFn: () => orgAPI.fileRecords.listSharedWithMe(),
    ...options,
  });

  useEffect(() => {
    if (!query.data) return;
    for (const r of query.data) {
      queryClient.setQueryData(fileRecordKeys.detail(r.id), r);
    }
  }, [query.data, queryClient]);

  return query;
}

/**
 * Fetch all `class = "canvas"` file records pinned to a canvas.
 *
 * Mirrors `useWorkspaceFileRecords` for the canvas explorer. Listing is
 * intentionally one-shot; explicit mutations invalidate the canvas key.
 *
 * Public share SPA has no API auth — skip the list and rely on CDN-enriched
 * entity payloads (same gate as `useHydrateFileRecordEntity`).
 */
export function useCanvasFileRecords(
  canvasId: string | undefined,
  options?: Partial<UseQueryOptions<FileRecord[], AxiosError>>,
) {
  const isPublicCanvasView = useIsPublicCanvasView();
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: fileRecordKeys.canvas(canvasId ?? ""),
    queryFn: () => {
      if (!canvasId) throw new Error("Canvas ID required");
      return orgAPI.fileRecords.list({
        class: "canvas",
        canvas_id: canvasId,
      });
    },
    ...options,
    enabled: !!canvasId && !isPublicCanvasView && (options?.enabled ?? true),
  });

  useEffect(() => {
    if (!query.data) return;
    for (const r of query.data) {
      queryClient.setQueryData(fileRecordKeys.detail(r.id), r);
    }
  }, [query.data, queryClient]);

  return query;
}

/**
 * Fetch all `class = "flow-session"` file records for a workspace chat.
 *
 * Mirrors `useCanvasFileRecords` for the Files-rail context section.
 */
export function useFlowSessionFileRecords(
  flowSessionId: string | undefined,
  options?: Partial<UseQueryOptions<FileRecord[], AxiosError>>,
) {
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: fileRecordKeys.flowSession(flowSessionId ?? ""),
    queryFn: () => {
      if (!flowSessionId) throw new Error("Flow session ID required");
      return orgAPI.fileRecords.list({
        class: "flow-session",
        flow_session_id: flowSessionId,
      });
    },
    ...options,
    enabled: !!flowSessionId && (options?.enabled ?? true),
  });

  useEffect(() => {
    if (!query.data) return;
    for (const r of query.data) {
      queryClient.setQueryData(fileRecordKeys.detail(r.id), r);
    }
  }, [query.data, queryClient]);

  return query;
}

/**
 * List recently-deleted (recoverable) file records scoped to a workspace.
 *
 * The query intentionally does NOT populate `fileRecordKeys.detail(id)` —
 * the row carries `status: "deleted"` and partial fields, and would poison
 * the per-id cache that other surfaces (canvas tiles, detail pane) read.
 * Recovery surfaces always render straight from this list.
 */
export function useRecentlyDeletedWorkspaceFileRecords(
  workspaceId: string | undefined,
  options?: Partial<UseQueryOptions<FileRecord[], AxiosError>>,
) {
  return useQuery({
    queryKey: fileRecordKeys.recentlyDeleted("workspace", workspaceId ?? ""),
    queryFn: () => {
      if (!workspaceId) throw new Error("Workspace ID required");
      return orgAPI.fileRecords.list({
        class: "workspace",
        workspace_id: workspaceId,
        recently_deleted: true,
      });
    },
    ...options,
    enabled: !!workspaceId && (options?.enabled ?? true),
  });
}

/**
 * List recently-deleted (recoverable) file records pinned to a canvas. See
 * {@link useRecentlyDeletedWorkspaceFileRecords} for cache shape notes — the
 * canvas-scoped query mirrors it.
 */
export function useRecentlyDeletedCanvasFileRecords(
  canvasId: string | undefined,
  options?: Partial<UseQueryOptions<FileRecord[], AxiosError>>,
) {
  return useQuery({
    queryKey: fileRecordKeys.recentlyDeleted("canvas", canvasId ?? ""),
    queryFn: () => {
      if (!canvasId) throw new Error("Canvas ID required");
      return orgAPI.fileRecords.list({
        class: "canvas",
        canvas_id: canvasId,
        recently_deleted: true,
      });
    },
    ...options,
    enabled: !!canvasId && (options?.enabled ?? true),
  });
}

interface RecoverContext {
  /** Cache key for the deleted list we optimistically removed from. */
  deletedKey: readonly unknown[];
  /** Snapshot to put back if the mutation fails. */
  removedRecord?: FileRecord;
}

/**
 * Recover (un-delete) a file record. Optimistically removes the row from the
 * deleted-list cache and, on success, invalidates the matching active list
 * so the recovered record reappears at its original path.
 *
 * Caller supplies `{ scope, scopeId }` so the hook knows which deleted-list
 * cache to mutate and which active list to invalidate. We accept the full
 * record in the mutation variables (not just the id) so the optimistic
 * removal can find the row without a lookup.
 */
export function useRecoverFileRecord(
  scope: RecoverableScope,
  scopeId: string | undefined,
  options?: UseMutationOptions<FileRecord, AxiosError, { record: FileRecord }>,
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ record }: { record: FileRecord }) =>
      orgAPI.fileRecords.recover(record.id),
    ...options,
    onMutate: async ({ record }): Promise<RecoverContext> => {
      const deletedKey = fileRecordKeys.recentlyDeleted(scope, scopeId ?? "");
      await queryClient.cancelQueries({ queryKey: deletedKey });
      const current = queryClient.getQueryData<FileRecord[]>(deletedKey);
      const removedRecord = current?.find((r) => r.id === record.id);
      queryClient.setQueryData<FileRecord[]>(deletedKey, (prev) =>
        prev ? prev.filter((r) => r.id !== record.id) : prev,
      );
      return { deletedKey, removedRecord };
    },
    onError: (err, variables, context) => {
      const ctx = context as RecoverContext | undefined;
      if (ctx?.removedRecord && ctx.deletedKey) {
        const restored = ctx.removedRecord;
        queryClient.setQueryData<FileRecord[]>(ctx.deletedKey, (prev) => {
          if (!prev) return prev;
          if (prev.some((r) => r.id === restored.id)) return prev;
          return [...prev, restored];
        });
      }
      options?.onError?.(err, variables, context);
    },
    onSuccess: (recovered, variables, context) => {
      // Drop any stale per-id snapshot we might have around — the recovered
      // record carries fresh presigned URLs and a flipped status.
      queryClient.setQueryData(fileRecordKeys.detail(recovered.id), recovered);

      // Repopulate the active list at the right scope. We invalidate rather
      // than splice manually because the server already publishes a
      // file_record event the SSE handler also processes, and an explicit
      // refetch is the simplest way to converge with that path.
      if (recovered.class === "workspace" && recovered.workspace_id) {
        queryClient.invalidateQueries({
          queryKey: fileRecordKeys.workspace(recovered.workspace_id),
        });
      } else if (recovered.class === "canvas" && recovered.canvas_id) {
        queryClient.invalidateQueries({
          queryKey: fileRecordKeys.canvas(recovered.canvas_id),
        });
      }
      options?.onSuccess?.(recovered, variables, context);
    },
    onSettled: (data, error, variables, context) => {
      if (scopeId) {
        queryClient.invalidateQueries({
          queryKey: fileRecordKeys.recentlyDeleted(scope, scopeId),
        });
      }
      queryClient.invalidateQueries({ queryKey: fileRecordKeys.recent() });
      options?.onSettled?.(data, error, variables, context);
    },
  });
}

export function useCreateFileRecord(
  workspaceId: string | undefined,
  options?: UseMutationOptions<FileRecord, AxiosError, CreateFileRecordPayload>,
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateFileRecordPayload) =>
      orgAPI.fileRecords.create(payload),
    ...options,
    onSuccess: (...args) => {
      if (workspaceId) {
        queryClient.invalidateQueries({
          queryKey: fileRecordKeys.workspace(workspaceId),
        });
      }
      queryClient.invalidateQueries({ queryKey: fileRecordKeys.recent() });
      options?.onSuccess?.(...args);
    },
  });
}

/**
 * Note on optimistic updates and the SSE handler:
 *
 * The org event stream (`useHandleWebSocketEvent`) writes incoming
 * `file_record.*` events directly into the workspace list cache. Mutations
 * here apply the same change locally before the request fires so the UI
 * feels instant. The SSE event that follows is then idempotent — the
 * record we optimistically removed is already gone, the path we
 * optimistically updated already matches.
 *
 * Rollback is intentionally surgical (re-insert / restore *only* the
 * record this mutation touched, and only if it's still missing) so that
 * concurrent fan-outs — e.g. cascade folder delete via `Promise.all` —
 * don't roll back records that other concurrent mutations have already
 * removed.
 *
 * `onSettled` invalidates the workspace query as a backstop for the rare
 * case where the websocket missed the event (reconnect window). The user
 * already sees the optimistic state, so the silent background refetch is
 * essentially free.
 */

interface DeleteContext {
  /** Snapshot of the record we removed, so we can put it back on failure. */
  removedRecord?: FileRecord;
  workspaceKey?: readonly unknown[];
}

export function useDeleteFileRecord(
  workspaceId: string | undefined,
  options?: UseMutationOptions<void, AxiosError, string>,
) {
  const queryClient = useQueryClient();
  const location = useLocation();
  const closePane = useClosePane();
  return useMutation({
    mutationFn: (fileRecordId: string) =>
      orgAPI.fileRecords.delete(fileRecordId),
    ...options,
    onMutate: async (fileRecordId): Promise<DeleteContext> => {
      if (!workspaceId) return {};
      const workspaceKey = fileRecordKeys.workspace(workspaceId);
      await queryClient.cancelQueries({ queryKey: workspaceKey });
      const current = queryClient.getQueryData<FileRecord[]>(workspaceKey);
      const removedRecord = current?.find((r) => r.id === fileRecordId);
      queryClient.setQueryData<FileRecord[]>(workspaceKey, (prev) =>
        prev ? prev.filter((r) => r.id !== fileRecordId) : prev,
      );
      return { removedRecord, workspaceKey };
    },
    onError: (err, fileRecordId, context) => {
      const ctx = context as DeleteContext | undefined;
      if (ctx?.removedRecord && ctx.workspaceKey) {
        queryClient.setQueryData<FileRecord[]>(ctx.workspaceKey, (prev) => {
          if (!prev) return prev;
          if (prev.some((r) => r.id === fileRecordId)) return prev;
          return [...prev, ctx.removedRecord!];
        });
      }
      options?.onError?.(err, fileRecordId, context);
    },
    onSuccess: (data, variables, context) => {
      options?.onSuccess?.(data, variables, context);

      // If the open pane matches the file record detail route that is being deleted, close it.
      if (
        location.state?.rightPane?.pathname ===
        route(ROUTES.FILE_RECORDS.DETAIL, { fileRecordId: variables })
      ) {
        closePane("rightPane");
      }
    },
    onSettled: (data, error, variables, context) => {
      if (workspaceId) {
        queryClient.invalidateQueries({
          queryKey: fileRecordKeys.workspace(workspaceId),
        });
        // The deleted record now belongs in the recently-deleted list.
        // Without this, the global 10s staleTime serves a stale cached
        // list and the just-deleted file is missing from the recovery
        // sheet if it was opened within that window.
        queryClient.invalidateQueries({
          queryKey: fileRecordKeys.recentlyDeleted("workspace", workspaceId),
        });
      }
      queryClient.invalidateQueries({ queryKey: fileRecordKeys.recent() });
      options?.onSettled?.(data, error, variables, context);
    },
  });
}

interface MoveContext {
  /** Source cache key (may equal destKey for in-place moves). */
  sourceKey?: readonly unknown[];
  /** Destination cache key, only set for cross-scope moves. */
  destKey?: readonly unknown[];
  /**
   * The pre-mutation record snapshot from the source list. We restore
   * just this entry on failure rather than the whole array so concurrent
   * mutations aren't clobbered.
   */
  originalRecord?: FileRecord;
  /**
   * The optimistically applied record (with the new path/class). Used to
   * identify and remove the entry from the destination list on rollback.
   */
  optimisticRecord?: FileRecord;
}

export type FileRecordListScope =
  | { class: "workspace"; workspace_id: string }
  | { class: "canvas"; canvas_id: string }
  | { class: "flow-session"; flow_session_id: string };

type MoveFileRecordSource = string | FileRecordListScope | undefined;

function queryKeysEqual(
  a: readonly unknown[] | undefined,
  b: readonly unknown[] | undefined,
): boolean {
  if (!a || !b || a.length !== b.length) return false;
  return a.every((part, index) => Object.is(part, b[index]));
}

function listKeyFromScope(
  scope: FileRecordListScope | undefined,
): readonly unknown[] | undefined {
  if (!scope) return undefined;
  if (scope.class === "workspace") {
    return fileRecordKeys.workspace(scope.workspace_id);
  }
  if (scope.class === "canvas") {
    return fileRecordKeys.canvas(scope.canvas_id);
  }
  return fileRecordKeys.flowSession(scope.flow_session_id);
}

function listKeyFromComponents(
  components: FileRecordComponents,
): readonly unknown[] | undefined {
  if (components.class === "workspace" && components.workspace_id) {
    return fileRecordKeys.workspace(components.workspace_id);
  }
  if (components.class === "canvas" && components.canvas_id) {
    return fileRecordKeys.canvas(components.canvas_id);
  }
  if (components.class === "flow-session" && components.flow_session_id) {
    return fileRecordKeys.flowSession(components.flow_session_id);
  }
  return undefined;
}

function normalizeMoveSource(
  source: MoveFileRecordSource,
): FileRecordListScope | undefined {
  if (!source) return undefined;
  if (typeof source === "string") {
    return { class: "workspace", workspace_id: source };
  }
  return source;
}

function applyDestinationToRecord(
  record: FileRecord,
  destination: FileRecordComponents,
): FileRecord {
  return {
    ...record,
    class: destination.class,
    workspace_id: destination.workspace_id,
    canvas_id: destination.canvas_id,
    flow_session_id: destination.flow_session_id,
    path: destination.path,
    updated_at: new Date().toISOString(),
  };
}

export function useMoveFileRecord(
  source: MoveFileRecordSource,
  options?: UseMutationOptions<
    FileRecord,
    AxiosError,
    { fileRecordId: string; payload: MoveFileRecordPayload }
  >,
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      fileRecordId,
      payload,
    }: {
      fileRecordId: string;
      payload: MoveFileRecordPayload;
    }) => orgAPI.fileRecords.move(fileRecordId, payload),
    ...options,
    onMutate: async ({ fileRecordId, payload }): Promise<MoveContext> => {
      const sourceScope = normalizeMoveSource(source);
      const sourceKey = listKeyFromScope(sourceScope);
      const destKey = listKeyFromComponents(payload.destination);
      const isSameList = queryKeysEqual(sourceKey, destKey);

      if (sourceKey) {
        await queryClient.cancelQueries({ queryKey: sourceKey });
      }
      if (destKey && !isSameList) {
        await queryClient.cancelQueries({ queryKey: destKey });
      }

      const sourceList = sourceKey
        ? queryClient.getQueryData<FileRecord[]>(sourceKey)
        : undefined;
      const originalRecord = sourceList?.find((r) => r.id === fileRecordId);
      if (!sourceKey || !originalRecord) return { sourceKey, destKey };

      const optimisticRecord = applyDestinationToRecord(
        originalRecord,
        payload.destination,
      );

      if (destKey && !isSameList) {
        // Cross-scope: remove from source, add to destination.
        queryClient.setQueryData<FileRecord[]>(sourceKey, (prev) =>
          prev ? prev.filter((r) => r.id !== fileRecordId) : prev,
        );
        queryClient.setQueryData<FileRecord[]>(destKey, (prev) => {
          if (!prev) return prev;
          if (prev.some((r) => r.id === fileRecordId)) return prev;
          return [...prev, optimisticRecord];
        });
      } else {
        // Same-scope: replace the record in place with the new path.
        queryClient.setQueryData<FileRecord[]>(sourceKey, (prev) =>
          prev
            ? prev.map((r) => (r.id === fileRecordId ? optimisticRecord : r))
            : prev,
        );
      }

      return { sourceKey, destKey, originalRecord, optimisticRecord };
    },
    onError: (err, variables, context) => {
      const ctx = context as MoveContext | undefined;
      if (ctx?.originalRecord && ctx.sourceKey) {
        const original = ctx.originalRecord;
        queryClient.setQueryData<FileRecord[]>(ctx.sourceKey, (prev) => {
          if (!prev) return prev;
          if (prev.some((r) => r.id === original.id)) {
            return prev.map((r) => (r.id === original.id ? original : r));
          }
          return [...prev, original];
        });
        if (ctx.destKey && !queryKeysEqual(ctx.destKey, ctx.sourceKey)) {
          queryClient.setQueryData<FileRecord[]>(ctx.destKey, (prev) =>
            prev ? prev.filter((r) => r.id !== original.id) : prev,
          );
        }
      }
      options?.onError?.(err, variables, context);
    },
    onSuccess: (data, variables, context) => {
      options?.onSuccess?.(data, variables, context);
    },
    onSettled: (data, error, variables, context) => {
      const ctx = context as MoveContext | undefined;
      if (ctx?.sourceKey) {
        queryClient.invalidateQueries({ queryKey: ctx.sourceKey });
      }
      // Cross-scope move: refresh the destination scope too in case
      // the SSE event missed.
      if (ctx?.destKey && !queryKeysEqual(ctx.destKey, ctx.sourceKey)) {
        queryClient.invalidateQueries({ queryKey: ctx.destKey });
      }
      queryClient.invalidateQueries({ queryKey: fileRecordKeys.recent() });
      options?.onSettled?.(data, error, variables, context);
    },
  });
}

interface RenameContext {
  /** Per-id detail cache key — always present. */
  detailKey: readonly unknown[];
  /** Pre-mutation per-id snapshot for rollback. */
  previousDetail?: FileRecord;
  /** List cache key for the record's class scope (workspace / canvas / flow-session). */
  listKey?: readonly unknown[];
  /** Pre-mutation list snapshot — only filled when we patched a list optimistically. */
  previousList?: FileRecord[];
}

/**
 * Rename a file record in place. This is a thin wrapper over `move` with
 * `destination` set to the source's own scope (same class, same workspace /
 * canvas / flow_session id), changing only the leaf segment of `path`.
 *
 * Why a dedicated hook (vs. calling `useMoveFileRecord` directly)?
 *
 *   1. **Per-id detail cache.** The detail pane reads
 *      `fileRecordKeys.detail(id)` for the live presigned `url`. Move
 *      changes the S3 key, which invalidates that URL — so on success we
 *      write the server response (with fresh URLs) into the per-id cache.
 *      `useMoveFileRecord` only patches the workspace list, leaving the
 *      detail cache stale and the active <img>/<video>/<iframe> 403'd
 *      until the next refetch.
 *
 *   2. **Class-aware list invalidation.** `useMoveFileRecord` is
 *      hard-coded around `workspaceId`. Renames happen across all three
 *      classes (workspace / canvas / flow-session); this hook routes
 *      list invalidation by `record.class`.
 *
 *   3. **Optimistic name update.** Patches the per-id detail cache
 *      synchronously so the pane header doesn't flash the old name
 *      between Enter and the websocket-driven re-render.
 *
 * Caller passes the *full* current record (not just an id) so we have
 * `class` + scope IDs without a second fetch.
 */
export function useRenameFileRecord(
  options?: UseMutationOptions<
    FileRecord,
    AxiosError,
    { record: FileRecord; newPath: string }
  >,
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      record,
      newPath,
    }: {
      record: FileRecord;
      newPath: string;
    }) => {
      const payload: MoveFileRecordPayload = {
        destination: {
          class: record.class,
          workspace_id: record.workspace_id,
          canvas_id: record.canvas_id,
          flow_session_id: record.flow_session_id,
          path: newPath,
          is_folder: record.path.endsWith("/") || undefined,
        },
      };
      return orgAPI.fileRecords.move(record.id, payload);
    },
    ...options,
    onMutate: async ({ record, newPath }): Promise<RenameContext> => {
      const detailKey = fileRecordKeys.detail(record.id);

      // Order matters: snapshot and write the optimistic record
      // SYNCHRONOUSLY before any `await`. The standard tanstack pattern
      // is `await cancelQueries` first, but the await yields the
      // microtask, and React commits the caller's batched state update
      // (e.g. `setIsEditing(false)` in the rename input) during that
      // gap. The newly-read-only header would then read `record.path`
      // from a not-yet-patched cache and flash the old name for one
      // frame before the optimistic write lands. Doing the patch first
      // means the next React render already sees the new path.
      const previousDetail =
        queryClient.getQueryData<FileRecord>(detailKey) ?? record;
      const optimistic: FileRecord = {
        ...previousDetail,
        path: newPath,
        updated_at: new Date().toISOString(),
      };
      queryClient.setQueryData<FileRecord>(detailKey, optimistic);
      // Now cancel any in-flight refetch for this key so it can't
      // resolve and clobber what we just wrote.
      await queryClient.cancelQueries({ queryKey: detailKey });

      const listKey =
        record.class === "workspace" && record.workspace_id
          ? fileRecordKeys.workspace(record.workspace_id)
          : record.class === "canvas" && record.canvas_id
            ? fileRecordKeys.canvas(record.canvas_id)
            : record.class === "flow-session" && record.flow_session_id
              ? fileRecordKeys.flowSession(record.flow_session_id)
              : undefined;

      let previousList: FileRecord[] | undefined;
      if (listKey) {
        previousList = queryClient.getQueryData<FileRecord[]>(listKey);
        if (previousList) {
          queryClient.setQueryData<FileRecord[]>(listKey, (prev) =>
            prev
              ? prev.map((r) => (r.id === record.id ? optimistic : r))
              : prev,
          );
        }
        await queryClient.cancelQueries({ queryKey: listKey });
      }

      return { detailKey, previousDetail, listKey, previousList };
    },
    onError: (err, variables, context) => {
      const ctx = context as RenameContext | undefined;
      if (ctx?.detailKey && ctx.previousDetail) {
        queryClient.setQueryData(ctx.detailKey, ctx.previousDetail);
      }
      if (ctx?.listKey && ctx.previousList) {
        queryClient.setQueryData(ctx.listKey, ctx.previousList);
      }
      options?.onError?.(err, variables, context);
    },
    onSuccess: (data, variables, context) => {
      // Server response carries freshly-presigned `url` / `download_url`
      // for the new S3 key — write it back so the active viewer stops
      // pointing at the now-403ing old key.
      queryClient.setQueryData(fileRecordKeys.detail(data.id), data);
      options?.onSuccess?.(data, variables, context);
    },
    onSettled: (data, error, variables, context) => {
      const ctx = context as RenameContext | undefined;
      if (ctx?.listKey) {
        queryClient.invalidateQueries({ queryKey: ctx.listKey });
      }
      queryClient.invalidateQueries({ queryKey: fileRecordKeys.recent() });
      options?.onSettled?.(data, error, variables, context);
    },
  });
}

interface CopyContext {
  /**
   * Cache key for the destination scope's listing. Set for any
   * `destination.class` we know how to list.
   */
  destKey?: readonly unknown[];
}

function destinationListKey(
  payload: CopyFileRecordPayload,
): readonly unknown[] | null {
  return listKeyFromComponents(payload.destination) ?? null;
}

export function useCopyFileRecord(
  workspaceId: string | undefined,
  options?: UseMutationOptions<
    FileRecord,
    AxiosError,
    { fileRecordId: string; payload: CopyFileRecordPayload }
  >,
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      fileRecordId,
      payload,
    }: {
      fileRecordId: string;
      payload: CopyFileRecordPayload;
    }) => orgAPI.fileRecords.copy(fileRecordId, payload),
    ...options,
    onMutate: async ({ payload }): Promise<CopyContext> => {
      // Copy mints a brand-new record id we don't know yet, so there's no
      // safe optimistic insert. We just preempt the query to prevent stale
      // refetches from racing the SSE-driven cache writes, then let the
      // `file_record.created` event populate the destination list.
      const destKey = destinationListKey(payload);
      if (!destKey) return {};
      await queryClient.cancelQueries({ queryKey: destKey });
      return { destKey };
    },
    onSuccess: (data, variables, context) => {
      options?.onSuccess?.(data, variables, context);
    },
    onSettled: (data, error, variables, context) => {
      const ctx = context as CopyContext | undefined;
      if (workspaceId) {
        queryClient.invalidateQueries({
          queryKey: fileRecordKeys.workspace(workspaceId),
        });
      }
      if (ctx?.destKey) {
        queryClient.invalidateQueries({ queryKey: ctx.destKey });
      }
      queryClient.invalidateQueries({ queryKey: fileRecordKeys.recent() });
      options?.onSettled?.(data, error, variables, context);
    },
  });
}

/**
 * Publish a file record so its `public/{file_id}` URL becomes live.
 *
 * On success we update the per-id detail cache with the new publish state
 * so any pane already rendering this record (e.g. `HtmlContent`'s publish
 * button) reflects the change without a refetch.
 */
export function usePublishFileRecord(
  options?: UseMutationOptions<FileRecord, AxiosError, string>,
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (fileRecordId: string) =>
      orgAPI.fileRecords.publish(fileRecordId),
    ...options,
    onSuccess: (record, variables, context) => {
      queryClient.setQueryData(fileRecordKeys.detail(record.id), record);
      options?.onSuccess?.(record, variables, context);
    },
  });
}

export function useUnpublishFileRecord(
  options?: UseMutationOptions<FileRecord, AxiosError, string>,
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (fileRecordId: string) =>
      orgAPI.fileRecords.unpublish(fileRecordId),
    ...options,
    onSuccess: (record, variables, context) => {
      queryClient.setQueryData(fileRecordKeys.detail(record.id), record);
      options?.onSuccess?.(record, variables, context);
    },
  });
}

/**
 * Publish many file records in one mutation. Wraps `publishMany`'s
 * `Promise.allSettled` semantics so the caller receives per-id outcomes,
 * and writes every successful row into `fileRecordKeys.detail` so any pane
 * already rendering one of those records (publish badge, dialog row, …)
 * picks up the new publish state immediately — same behaviour as the
 * single-record `usePublishFileRecord` hook, just fanned out.
 */
export function usePublishFileRecords(
  options?: UseMutationOptions<PublishManyResult[], AxiosError, string[]>,
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (fileRecordIds: string[]) =>
      orgAPI.fileRecords.publishMany(fileRecordIds),
    ...options,
    onSuccess: (results, variables, context) => {
      for (const r of results) {
        if (r.status === "fulfilled") {
          queryClient.setQueryData(
            fileRecordKeys.detail(r.file_record.id),
            r.file_record,
          );
        }
      }
      options?.onSuccess?.(results, variables, context);
    },
  });
}

/**
 * Unpublish multiple file records in one call. Mirrors
 * `usePublishFileRecords` — same `Promise.allSettled` semantics, same
 * cache-update behaviour for fulfilled rows.
 */
export function useUnpublishFileRecords(
  options?: UseMutationOptions<UnpublishManyResult[], AxiosError, string[]>,
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (fileRecordIds: string[]) =>
      orgAPI.fileRecords.unpublishMany(fileRecordIds),
    ...options,
    onSuccess: (results, variables, context) => {
      for (const r of results) {
        if (r.status === "fulfilled") {
          queryClient.setQueryData(
            fileRecordKeys.detail(r.file_record.id),
            r.file_record,
          );
        }
      }
      options?.onSuccess?.(results, variables, context);
    },
  });
}

export function usePresignFileRecordUpload(
  options?: UseMutationOptions<PresignUploadResponse, AxiosError, string>,
) {
  return useMutation({
    mutationFn: (fileRecordId: string) =>
      orgAPI.fileRecords.presignUpload(fileRecordId),
    ...options,
  });
}

/**
 * Historical S3 object versions for a file record (last 30 days, processed
 * only, excluding current head — see {@link orgAPI.fileRecords.listVersions}).
 */
export function useFileRecordVersionsQuery(
  fileRecordId: string | undefined,
  options?: Pick<UseQueryOptions<FileRecordVersion[]>, "enabled">,
) {
  return useQuery({
    queryKey: fileRecordKeys.versions(fileRecordId ?? ""),
    queryFn: () => orgAPI.fileRecords.listVersions(fileRecordId!),
    enabled: !!fileRecordId && (options?.enabled ?? true),
    staleTime: 60_000,
    refetchOnWindowFocus: false,
  });
}

export function useRestoreFileRecordVersionMutation(
  options?: UseMutationOptions<
    FileRecord,
    AxiosError,
    { fileRecordId: string; fileRecordVersionId: string }
  >,
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ fileRecordId, fileRecordVersionId }) =>
      orgAPI.fileRecords.restoreVersion(fileRecordId, fileRecordVersionId),
    ...options,
    onSuccess: (record, variables, context) => {
      queryClient.setQueryData(fileRecordKeys.detail(record.id), record);
      queryClient.invalidateQueries({
        queryKey: fileRecordKeys.versions(variables.fileRecordId),
      });
      options?.onSuccess?.(record, variables, context);
    },
  });
}

/**
 * Patch the innerHTML of a named content region (`data-edit-id` element) within an HTML file record.
 * On success the per-id cache is invalidated so the detail pane re-fetches
 * a fresh presigned URL; the SSE `file_record.updated` event drives the
 * actual content re-fetch inside `HtmlContent`.
 */
export function usePatchFileRecordRegion(
  fileRecordId: string,
  options?: UseMutationOptions<
    PatchFileRecordRegionResponse,
    AxiosError,
    PatchFileRecordRegionPayload
  >,
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: PatchFileRecordRegionPayload) =>
      orgAPI.fileRecords.patchRegion(fileRecordId, payload),
    ...options,
    onSuccess: (...args) => {
      queryClient.invalidateQueries({
        queryKey: fileRecordKeys.detail(fileRecordId),
      });
      options?.onSuccess?.(...args);
    },
  });
}
